
import { PythonModule } from '../types';

export const PYTHON_TESTING_MODULE: PythonModule = {
  id: "python-testing",
  title: "Testing and Test-Driven Development",
  description: "Master testing in Python with unittest, pytest, and test-driven development practices.",
  difficulty: "intermediate",
  content: `
# Testing and Test-Driven Development

Testing is crucial for building reliable, maintainable Python applications. Learn to write effective tests and follow TDD practices.

## Introduction to Testing

### Why Testing Matters

\`\`\`python
# Example: Function without tests (risky)
def calculate_discount(price, discount_percent):
    return price * (1 - discount_percent / 100)

# What happens with edge cases?
# calculate_discount(100, -10)  # Negative discount?
# calculate_discount(100, 150)  # Over 100% discount?
# calculate_discount("100", 10)  # String input?
\`\`\`

## unittest Framework

### Basic Test Structure

\`\`\`python
import unittest

class Calculator:
    def add(self, a, b):
        return a + b
    
    def divide(self, a, b):
        if b == 0:
            raise ValueError("Cannot divide by zero")
        return a / b
    
    def is_even(self, number):
        return number % 2 == 0

class TestCalculator(unittest.TestCase):
    def setUp(self):
        """Set up test fixtures before each test method."""
        self.calculator = Calculator()
    
    def test_add_positive_numbers(self):
        result = self.calculator.add(3, 5)
        self.assertEqual(result, 8)
    
    def test_add_negative_numbers(self):
        result = self.calculator.add(-3, -5)
        self.assertEqual(result, -8)
    
    def test_divide_normal_case(self):
        result = self.calculator.divide(10, 2)
        self.assertEqual(result, 5.0)
    
    def test_divide_by_zero_raises_error(self):
        with self.assertRaises(ValueError):
            self.calculator.divide(10, 0)
    
    def test_is_even_with_even_number(self):
        self.assertTrue(self.calculator.is_even(4))
    
    def test_is_even_with_odd_number(self):
        self.assertFalse(self.calculator.is_even(3))
    
    def tearDown(self):
        """Clean up after each test method."""
        pass

if __name__ == '__main__':
    unittest.main()
\`\`\`

### Common Assertion Methods

\`\`\`python
class TestAssertions(unittest.TestCase):
    def test_various_assertions(self):
        # Equality
        self.assertEqual(2 + 2, 4)
        self.assertNotEqual(2 + 2, 5)
        
        # Boolean
        self.assertTrue(True)
        self.assertFalse(False)
        
        # None
        self.assertIsNone(None)
        self.assertIsNotNone("Hello")
        
        # Membership
        self.assertIn('a', 'apple')
        self.assertNotIn('z', 'apple')
        
        # Numeric comparisons
        self.assertGreater(5, 3)
        self.assertLess(3, 5)
        self.assertAlmostEqual(0.1 + 0.2, 0.3, places=7)
        
        # Collections
        self.assertListEqual([1, 2, 3], [1, 2, 3])
        self.assertDictEqual({'a': 1}, {'a': 1})
\`\`\`

## Test-Driven Development (TDD)

### TDD Cycle: Red-Green-Refactor

\`\`\`python
# Step 1: Write a failing test (RED)
class TestBankAccount(unittest.TestCase):
    def test_initial_balance_is_zero(self):
        account = BankAccount()
        self.assertEqual(account.balance, 0)

# Step 2: Write minimal code to pass (GREEN)
class BankAccount:
    def __init__(self):
        self.balance = 0

# Step 3: Refactor (if needed)
# Code is already clean for this simple case

# Continue TDD cycle...
class TestBankAccount(unittest.TestCase):
    def setUp(self):
        self.account = BankAccount()
    
    def test_initial_balance_is_zero(self):
        self.assertEqual(self.account.balance, 0)
    
    def test_deposit_increases_balance(self):
        self.account.deposit(100)
        self.assertEqual(self.account.balance, 100)
    
    def test_withdraw_decreases_balance(self):
        self.account.deposit(100)
        self.account.withdraw(30)
        self.assertEqual(self.account.balance, 70)
    
    def test_withdraw_more_than_balance_raises_error(self):
        with self.assertRaises(ValueError):
            self.account.withdraw(100)

class BankAccount:
    def __init__(self):
        self.balance = 0
    
    def deposit(self, amount):
        if amount <= 0:
            raise ValueError("Deposit amount must be positive")
        self.balance += amount
    
    def withdraw(self, amount):
        if amount <= 0:
            raise ValueError("Withdrawal amount must be positive")
        if amount > self.balance:
            raise ValueError("Insufficient funds")
        self.balance -= amount
\`\`\`

## pytest Framework

### Installing and Basic Usage

\`\`\`bash
# Install pytest
pip install pytest

# Run tests
pytest test_file.py
pytest  # Run all tests in current directory
\`\`\`

### pytest Test Examples

\`\`\`python
# test_math_operations.py
import pytest

def add(a, b):
    return a + b

def divide(a, b):
    if b == 0:
        raise ZeroDivisionError("Cannot divide by zero")
    return a / b

# Simple test functions (no class needed)
def test_add():
    assert add(2, 3) == 5
    assert add(-1, 1) == 0

def test_divide():
    assert divide(10, 2) == 5
    assert divide(9, 3) == 3

def test_divide_by_zero():
    with pytest.raises(ZeroDivisionError):
        divide(10, 0)

# Parametrized tests
@pytest.mark.parametrize("a,b,expected", [
    (2, 3, 5),
    (-1, 1, 0),
    (0, 0, 0),
    (100, -50, 50)
])
def test_add_parametrized(a, b, expected):
    assert add(a, b) == expected
\`\`\`

### Fixtures

\`\`\`python
import pytest

@pytest.fixture
def sample_data():
    """Provide test data for multiple tests."""
    return {
        'users': ['alice', 'bob', 'charlie'],
        'scores': [85, 92, 78]
    }

@pytest.fixture
def temp_file(tmp_path):
    """Create a temporary file for testing."""
    file_path = tmp_path / "test_file.txt"
    file_path.write_text("Hello, World!")
    return file_path

def test_user_data(sample_data):
    assert len(sample_data['users']) == 3
    assert 'alice' in sample_data['users']

def test_file_reading(temp_file):
    content = temp_file.read_text()
    assert content == "Hello, World!"
\`\`\`

## Mocking and Test Doubles

\`\`\`python
from unittest.mock import Mock, patch
import requests

class WeatherService:
    def get_temperature(self, city):
        response = requests.get(f"http://api.weather.com/{city}")
        return response.json()['temperature']

class TestWeatherService(unittest.TestCase):
    @patch('requests.get')
    def test_get_temperature(self, mock_get):
        # Mock the API response
        mock_response = Mock()
        mock_response.json.return_value = {'temperature': 25}
        mock_get.return_value = mock_response
        
        service = WeatherService()
        temp = service.get_temperature('London')
        
        self.assertEqual(temp, 25)
        mock_get.assert_called_once_with("http://api.weather.com/London")
\`\`\`

## Testing Best Practices

### Test Organization

\`\`\`python
# tests/
#   __init__.py
#   test_models.py
#   test_services.py
#   test_utils.py
#   fixtures/
#     sample_data.json

# conftest.py (pytest configuration)
import pytest

@pytest.fixture(scope="session")
def database():
    # Setup test database
    db = create_test_database()
    yield db
    # Cleanup
    db.close()
\`\`\`

### Coverage Analysis

\`\`\`bash
# Install coverage
pip install coverage

# Run tests with coverage
coverage run -m pytest
coverage report
coverage html  # Generate HTML report
\`\`\`

## Integration Testing

\`\`\`python
class TestUserRegistration(unittest.TestCase):
    def setUp(self):
        self.app = create_test_app()
        self.db = create_test_database()
    
    def test_complete_user_registration_flow(self):
        # Test the entire registration process
        user_data = {
            'username': 'testuser',
            'email': 'test@example.com',
            'password': 'securepassword123'
        }
        
        # Register user
        response = self.app.register_user(user_data)
        self.assertEqual(response.status_code, 201)
        
        # Verify user in database
        user = self.db.get_user_by_username('testuser')
        self.assertIsNotNone(user)
        self.assertEqual(user.email, 'test@example.com')
        
        # Test login
        login_response = self.app.login('testuser', 'securepassword123')
        self.assertEqual(login_response.status_code, 200)
\`\`\`

## Best Practices

1. **Write tests first** (TDD approach)
2. **Keep tests simple and focused** - one assertion per test when possible
3. **Use descriptive test names** that explain what is being tested
4. **Test edge cases and error conditions**
5. **Use fixtures and mocks** to isolate units under test
6. **Aim for high test coverage** but focus on quality over quantity
7. **Run tests frequently** and automate them in CI/CD pipelines

## Practice Exercise

Build a library management system with full test coverage, including user registration, book checkout/return, and late fee calculations.
`,
  duration: "75 minutes",
  objectives: [
    "Understand testing principles and benefits",
    "Master unittest and pytest frameworks",
    "Apply test-driven development practices",
    "Use mocking for external dependencies",
    "Implement integration and unit tests",
    "Measure and improve test coverage"
  ],
  order: 8
};
